import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import MessageComposer from './components/MessageComposer';
import MessageTimeline from './components/MessageTimeline';
import TemplateManager from './components/TemplateManager';
import ScheduledMessages from './components/ScheduledMessages';
import IntegrationStatus from './components/IntegrationStatus';
import CommunicationAnalytics from './components/CommunicationAnalytics';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';


const CommunicationCenter = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { currentLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('compose');

  const handleLogout = () => {
    console.log('Logout clicked');
  };

  const handleLanguageChange = (language) => {
    console.log('Language changed to:', language);
  };

  const mockUser = {
    name: 'Fatima El Mansouri',
    role: 'travel_agent',
    agency: 'Agence Al Baraka Travel',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
  };

  const mockNotifications = [
    {
      id: 'n1',
      title: t('communication.messageDelivered'),
      message: t('communication.messageDeliveredDesc'),
      timestamp: new Date(Date.now() - 300000),
      priority: 'low',
      read: false
    },
    {
      id: 'n2',
      title: t('communication.deliveryFailed'),
      message: t('communication.deliveryFailedDesc'),
      timestamp: new Date(Date.now() - 900000),
      priority: 'high',
      read: false
    }
  ];

  const mockMessages = [
    {
      id: 'm1',
      type: 'email',
      subject: 'Documents requis pour votre voyage Omra',
      preview: 'Cher(e) pèlerin(e), nous vous rappelons que les documents suivants sont nécessaires pour finaliser votre dossier...',
      recipientCount: 45,
      status: 'delivered',
      responseRate: 78,
      timestamp: new Date(Date.now() - 3600000)
    },
    {
      id: 'm2',
      type: 'whatsapp',
      subject: 'Rappel : Départ dans 7 jours',
      preview: 'Votre départ approche ! Nous vous rappelons les informations importantes concernant votre voyage...',
      recipientCount: 120,
      status: 'read',
      responseRate: 92,
      timestamp: new Date(Date.now() - 7200000)
    },
    {
      id: 'm3',
      type: 'sms',
      subject: 'Mise à jour visa',
      preview: 'Votre visa a été approuvé. Vous pouvez le récupérer à notre agence dès demain...',
      recipientCount: 38,
      status: 'delivered',
      responseRate: 85,
      timestamp: new Date(Date.now() - 10800000)
    },
    {
      id: 'm4',
      type: 'email',
      subject: 'Rappel de paiement - Solde restant',
      preview: 'Nous vous rappelons que le solde de votre voyage doit être réglé avant le 15 février 2026...',
      recipientCount: 23,
      status: 'pending',
      timestamp: new Date(Date.now() - 14400000)
    }
  ];

  const mockScheduledMessages = [
    {
      id: 's1',
      type: 'email',
      subject: 'Confirmation de réservation d\'hôtel',
      preview: 'Votre réservation d\'hôtel à Makkah a été confirmée pour la période du 20 au 27 mars 2026...',
      recipientCount: 45,
      scheduledDate: '2026-02-15',
      scheduledTime: '09:00'
    },
    {
      id: 's2',
      type: 'whatsapp',
      subject: 'Rappel de rendez-vous pour les vaccinations',
      preview: 'N\'oubliez pas votre rendez-vous pour les vaccinations obligatoires le 18 février 2026...',
      recipientCount: 32,
      scheduledDate: '2026-02-10',
      scheduledTime: '14:00'
    }
  ];

  const mockIntegrations = [
    {
      id: 'i1',
      name: 'Email SMTP',
      type: 'email',
      status: 'connected',
      lastSync: new Date(Date.now() - 1800000),
      messagesDelivered: 2456,
      deliveryRate: 98
    },
    {
      id: 'i2',
      name: 'WhatsApp Business',
      type: 'whatsapp',
      status: 'connected',
      lastSync: new Date(Date.now() - 900000),
      messagesDelivered: 1823,
      deliveryRate: 95
    },
    {
      id: 'i3',
      name: 'SMS Gateway',
      type: 'sms',
      status: 'connected',
      lastSync: new Date(Date.now() - 3600000),
      messagesDelivered: 987,
      deliveryRate: 92
    }
  ];

  const mockAnalytics = {
    totalSent: 5266,
    deliveryRate: 96,
    responseRate: 85,
    avgResponseTime: 2.5
  };

  const tabs = [
    { id: 'compose', label: { fr: t('communication.compose'), ar: t('communication.compose') }, icon: 'Edit' },
    { id: 'timeline', label: { fr: t('communication.timeline'), ar: t('communication.timeline') }, icon: 'MessageSquare' },
    { id: 'templates', label: { fr: t('communication.templates'), ar: t('communication.templates') }, icon: 'FileText' },
    { id: 'scheduled', label: { fr: t('communication.scheduled'), ar: t('communication.scheduled') }, icon: 'Clock' },
    { id: 'integrations', label: { fr: t('communication.integrations'), ar: t('communication.integrations') }, icon: 'Plug' },
    { id: 'analytics', label: { fr: t('communication.analytics'), ar: t('communication.analytics') }, icon: 'BarChart3' }
  ];

  const handleSendMessage = (messageData) => {
    console.log('Sending message:', messageData);
  };

  const handleSaveTemplate = (templateData) => {
    console.log('Saving template:', templateData);
  };

  const handleCancelSchedule = (messageId) => {
    console.log('Canceling scheduled message:', messageId);
  };

  const handleEditSchedule = (messageId) => {
    console.log('Editing scheduled message:', messageId);
  };

  const handleMessageClick = (message) => {
    console.log('Message clicked:', message);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={`transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'
        }`}
      >
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center lg:hidden">
                <Icon name="MessageSquare" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-foreground">
                  {t('communication.title')}
                </h1>
                <p className="caption text-muted-foreground hidden sm:block">
                  {currentLanguage === 'fr' ?'Gérez toutes vos communications avec les pèlerins' :'إدارة جميع اتصالاتك مع الحجاج'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                currentLanguage={currentLanguage}
                onNotificationClick={(notification) => console.log('Notification clicked:', notification)}
                onMarkAsRead={(notificationId) => console.log('Mark as read:', notificationId)}
              />
              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage}
              />
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="mb-6 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-md transition-smooth whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-warm'
                      : 'bg-card text-foreground hover:bg-muted border border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="font-medium">{tab?.label?.[currentLanguage]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {activeTab === 'compose' && (
              <MessageComposer
                currentLanguage={currentLanguage}
                onSendMessage={handleSendMessage}
              />
            )}

            {activeTab === 'timeline' && (
              <MessageTimeline
                messages={mockMessages}
                currentLanguage={currentLanguage}
                onMessageClick={handleMessageClick}
              />
            )}

            {activeTab === 'templates' && (
              <TemplateManager
                currentLanguage={currentLanguage}
                onSaveTemplate={handleSaveTemplate}
              />
            )}

            {activeTab === 'scheduled' && (
              <ScheduledMessages
                scheduledMessages={mockScheduledMessages}
                currentLanguage={currentLanguage}
                onCancelSchedule={handleCancelSchedule}
                onEditSchedule={handleEditSchedule}
              />
            )}

            {activeTab === 'integrations' && (
              <IntegrationStatus
                integrations={mockIntegrations}
                currentLanguage={currentLanguage}
              />
            )}

            {activeTab === 'analytics' && (
              <CommunicationAnalytics
                analytics={mockAnalytics}
                currentLanguage={currentLanguage}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CommunicationCenter;